import { isSessionInvalidationInProgress, claimExpiredRedirect } from "./sessionState";

/**
 * The sessionState guard functions the handler consults. Injectable so unit
 * tests can pass fakes instead of module-mocking sessionState (see the
 * Mocking Policy in docs/TESTING.md).
 */
export interface SessionGuards {
  isSessionInvalidationInProgress: () => boolean;
  claimExpiredRedirect: () => boolean;
}

/**
 * Factory that creates a guarded 401 redirect handler.
 *
 * Each app (admin, investor) calls this once at module scope, passing its
 * own Redux store and resetAuthState action creator. The returned function
 * can be called from Axios interceptors and React Query onError handlers.
 *
 * Guards (in order):
 * 1. Already on /login → no-op (don't redirect to self)
 * 2. Session-invalidation overlay in progress → no-op (different flow owns cleanup)
 * 3. Another 401 handler already claimed the redirect → no-op (prevents multiple reloads)
 *
 * `sessionGuards` defaults to the real sessionState module. Production callers
 * must NOT override it: the claimExpiredRedirect lock is shared with
 * useJwtExpiry's logout path, and both sides must consult the same instance
 * for the claim-once dedup to hold.
 */
export const createUnauthorizedRedirectHandler = (
  store: { dispatch: (action: any) => void },
  resetAuthState: () => { type: string },
  sessionGuards: SessionGuards = { isSessionInvalidationInProgress, claimExpiredRedirect },
): (() => void) => {
  return () => {
    if (window.location.pathname === "/login") return;
    if (sessionGuards.isSessionInvalidationInProgress()) return;
    if (!sessionGuards.claimExpiredRedirect()) return;

    store.dispatch(resetAuthState());
    sessionStorage.clear();
    sessionStorage.setItem("sessionExpired", "true");
    window.location.replace("/login");
  };
};
